import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useNavigateBack } from 'src/lib/hooks/useNavigateBack'
import BackButton from '../atoms/BackButton'
import { Spinner } from '@verisart/shared'
import t from '../../lib/translation'
import * as API from '../../api'
import { Blockchain, useGetAccountBlockchainAddresses } from '@verisart/nft/src'
import { truncateEthereumAddress } from '@verisart/shared'
import { ReactComponent as IconArrowDown } from '../../icons/ico-arrow-down.svg'
import { ReactComponent as IconWallet } from '../../icons/ico-wallet.svg'
import { ReactComponent as IconLogout } from '../../icons/ico-logout.svg'
import Popover from 'react-popover'
import Button from '../atoms/Button'
import { getAddOnRedirectLink } from 'src/pages/AddOnRedirect'
import { useAccount } from 'wagmi'
import { useConnectWallet } from '@verisart/nft/src/useConnectWallet'
import MoveWalletDialog from './MoveWalletDialog'
import CopyField from '../atoms/CopyField'
import Text from '../atoms/Text'
import { EMBEDDED_WALLET_ID } from '@verisart/nft/src/ConnectWalletContextProvider'

interface TopBarProps {
  showBackButton?: boolean
  showAddOn?: boolean
  blockchain?: Blockchain
}

const TopBar: React.FC<TopBarProps> = ({ showBackButton, showAddOn }) => {
  const navigateBack = useNavigateBack()
  const navigate = useNavigate()

  const { address: activeWallet, connector } = useAccount()
  const {
    connect,
    disconnect,
    showEmbedded,
    connecting,
    icon: connectorIcon,
  } = useConnectWallet()

  const { data: accountData } = useGetAccountBlockchainAddresses(
    API.customer.axios
  )
  const linkedWalletAddresses =
    accountData?.blockchainAddresses.map((addressObj) => addressObj.address) ||
    []
  const accountEmail = accountData?.realUserEmail

  const [isWalletPopoverOpen, setWalletPopoverOpen] = useState(false)
  const [isAccountPopoverOpen, setAccountPopoverOpen] = useState(false)
  const [isLinkedWalletsOpen, setLinkedWalletsOpen] = useState(false)
  const [isEmbeddedWalletLoading, setIsEmbeddedWalletLoading] = useState(false)

  const { id } = useParams<'id'>()

  return (
    <>
      <div className="ver-py-4 ver-px-7 ver-flex">
        {!showBackButton && (
          <BackButton
            onClick={(event) => {
              event.preventDefault()
              navigateBack('/')
            }}
          />
        )}

        <span className="ver-flex ver-flex-wrap-reverse ver-w-full ver-items-center ver-justify-end ver-gap-4">
          <span className="ver-flex ver-w-40 ver-items-center ver-justify-center">
            {showAddOn && (
              <div className="ver-w-44">
                <Button
                  fullWidth
                  onClick={async () =>
                    window.open(await getAddOnRedirectLink(id as string))
                  }
                >
                  {t.works.buyAddOn}
                </Button>
              </div>
            )}
          </span>

          <span className="ver-flex ver-w-44 ver-items-center ver-justify-center">
            {connecting ? (
              <Spinner />
            ) : (
              <>
                {!activeWallet && (
                  <button
                    className="ver-flex ver-items-center ver-gap-4"
                    type="button"
                    onClick={connect}
                  >
                    <IconWallet />
                    <span className="ver-text-base hover:ver-underline ver-underline-offset-4">
                      {t.topBar.connectWallet}
                    </span>
                  </button>
                )}
              </>
            )}

            {!connecting && activeWallet && (
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              <Popover
                isOpen={isWalletPopoverOpen}
                place="below"
                tipSize={0.01}
                onOuterAction={() => setWalletPopoverOpen(false)}
                style={{ backgroundColor: 'white' }}
                body={
                  <>
                    <div className="ver-mb-4">
                      <CopyField value={activeWallet}>
                        <Text as="span" variant="ver-text-base ver-font-medium">
                          {truncateEthereumAddress(activeWallet)}
                        </Text>
                      </CopyField>
                    </div>

                    {connector?.id === EMBEDDED_WALLET_ID &&
                      showEmbedded !== undefined && (
                        <div className="ver-flex ver-justify-start ver-mb-5">
                          {isEmbeddedWalletLoading ? (
                            <Spinner />
                          ) : (
                            <button
                              className="ver-flex ver-items-center ver-gap-4 ver-font-medium hover:ver-underline ver-underline-offset-4"
                              type="button"
                              onClick={async () => {
                                try {
                                  setIsEmbeddedWalletLoading(true)
                                  await showEmbedded?.()
                                } catch (error) {
                                  console.error(
                                    "Couldn't show your wallet information: ",
                                    error
                                  )
                                } finally {
                                  setIsEmbeddedWalletLoading(false)
                                }
                              }}
                            >
                              <IconWallet />
                              {t.topBar.showEmbeddedWallet}
                            </button>
                          )}
                        </div>
                      )}

                    <div>
                      <button
                        className="ver-flex ver-items-center ver-gap-4 ver-font-medium hover:ver-underline ver-underline-offset-4"
                        type="button"
                        onClick={async () => {
                          try {
                            await disconnect()
                            setWalletPopoverOpen(false)
                          } catch (error) {
                            console.error('Error disconnecting wallet: ', error)
                          }
                        }}
                      >
                        <IconLogout />
                        {t.topBar.disconnect}
                      </button>
                    </div>
                  </>
                }
                className="ver-border ver-border-gray-300 ver-py-2 ver-px-6 ver-rounded-md ver-z-20"
              >
                <button
                  className="ver-flex ver-items-center ver-gap-2"
                  type="button"
                  onClick={() => {
                    setWalletPopoverOpen(!isWalletPopoverOpen)
                  }}
                >
                  {connectorIcon && connector && (
                    <img
                      src={connectorIcon}
                      alt={connector.name}
                      className="ver-h-4 ver-w-4 ver-rounded-md"
                    />
                  )}
                  <span className="ver-text-base ver-font-medium hover:ver-underline ver-underline-offset-4">
                    {truncateEthereumAddress(activeWallet)}
                  </span>
                  <IconArrowDown
                    className={`ver-transform  ${
                      isWalletPopoverOpen ? 'ver-rotate-180' : 'ver-rotate-0'
                    } ver-w-3`}
                  />
                </button>
              </Popover>
            )}
          </span>

          <span className="ver-flex ver-w-10 ver-items-center ver-justify-center">
            {accountData && (
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              <Popover
                isOpen={isAccountPopoverOpen}
                place="below"
                tipSize={0.01}
                onOuterAction={() => setAccountPopoverOpen(false)}
                style={{ backgroundColor: 'white' }}
                body={
                  <>
                    <div className="ver-text-base ver-font-semibold">
                      {accountEmail}
                    </div>

                    <div className="ver-mt-4 ver-text-sm">
                      <span
                        className="ver-flex ver-cursor-pointer"
                        onClick={() => {
                          setLinkedWalletsOpen(!isLinkedWalletsOpen)
                        }}
                      >
                        {t.topBar.linkedWallets}
                        <IconArrowDown
                          className={`ver-transform  ${
                            isLinkedWalletsOpen
                              ? 'ver-rotate-180'
                              : 'ver-rotate-0'
                          } ver-ml-2 ver-mt-2 ver-w-3`}
                        />
                      </span>
                      {isLinkedWalletsOpen && (
                        <>
                          {linkedWalletAddresses.map((address, index) => (
                            <div key={index}>
                              {truncateEthereumAddress(address)}
                            </div>
                          ))}
                        </>
                      )}
                    </div>

                    <button
                      className="ver-flex ver-items-center ver-gap-4 ver-mt-5 ver-font-medium hover:ver-underline ver-underline-offset-4"
                      type="button"
                      onClick={() => {
                        API.customer.setSessionToken(null, '')
                        navigate('/')
                      }}
                    >
                      <IconLogout />
                      {t.topBar.logOut}
                    </button>
                  </>
                }
                className="ver-border ver-border-gray-300 ver-py-2 ver-px-6 ver-rounded-md ver-z-20"
              >
                <div
                  onClick={() => {
                    setAccountPopoverOpen(!isAccountPopoverOpen)
                  }}
                  className="ver-h-10 ver-w-10 ver-cursor-pointer ver-rounded-full ver-bg-gradient-to-b ver-from-[#c1c1f7] ver-via-[#fdc862] ver-to-[#bbe9c5]"
                >
                  &nbsp;
                </div>
              </Popover>
            )}
          </span>
        </span>
      </div>
      <MoveWalletDialog />
    </>
  )
}

export default TopBar
